import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { LanguageProvider } from './context/LanguageContext';
import { Toaster } from './components/ui/sonner';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import PricingPage from './pages/PricingPage';
import SellJunkCarPage from './pages/SellJunkCarPage';
import BuyerDashboard from './pages/BuyerDashboard';
import AdminDashboard from './pages/AdminDashboard';
import AdminPortalPage from './pages/AdminPortalPage';
import ComponentsDemo from './pages/ComponentsDemo';

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [], adminOnly = false }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="animate-pulse text-[#52525B]">Loading...</div>
      </div>
    );
  }
  
  if (user === false) {
    // Redirect unauthenticated admin route access to admin portal
    if (adminOnly) return <Navigate to="/admin-portal" replace />;
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}

// Public Route (redirects if already logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <div className="animate-pulse text-[#52525B]">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    if (user.role === 'buyer') return <Navigate to="/buyer" replace />;
    return <Navigate to="/dashboard" replace />;
  }

  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/sell-junk-car" element={<SellJunkCarPage />} />
      <Route path="/components" element={<ComponentsDemo />} />
      
      {/* Admin Portal - separate entry point */}
      <Route path="/admin-portal" element={<AdminPortalPage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={
        <PublicRoute><LoginPage /></PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute><RegisterPage /></PublicRoute>
      } />
      <Route path="/forgot-password" element={
        <PublicRoute><ForgotPasswordPage /></PublicRoute>
      } />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['dealer', 'admin']}>
          <DashboardPage />
        </ProtectedRoute>
      } />
      
      <Route path="/buyer" element={
        <ProtectedRoute allowedRoles={['buyer', 'admin']}>
          <BuyerDashboard />
        </ProtectedRoute>
      } />
      
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={['admin']} adminOnly>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <NotificationProvider>
          <AppRoutes />
          <Toaster position="top-right" />
          <PWAInstallPrompt />
          {/* Wakzy Digital World Badge */}
          <div className="fixed bottom-0 right-0 z-[99999] flex flex-col items-end" data-testid="wakzy-badge">
            <div className="bg-black text-white text-xs font-semibold px-5 py-3 rounded-tl-xl shadow-2xl flex items-center gap-2" style={{ minWidth: '200px', minHeight: '48px' }}>
              <span className="w-2 h-2 rounded-full bg-blue-400 animate-pulse" />
              Wakzy Digital World
            </div>
          </div>
        </NotificationProvider>
      </BrowserRouter>
    </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
